<template>
  <div
    ref="root"
    class="camera-tray"
    :class="{
      'camera-tray--floating': isFloating,
      'camera-tray--popout': isPopout,
      'camera-tray--fullscreen': isFullscreen,
      'camera-tray--has-maximized': maximizedId != null,
    }"
    :style="rootStyle"
    @mousedown="onTrayMouseDown"
  >
    <!-- Overlay full-viewport durante drag/resize para interceptar
         mouse events sobre los iframes de camaras (que si no los tragan
         y el drag/resize queda stuck). Se insertado via teleport-like
         estrategia: position fixed, inset 0, z-index muy alto. -->
    <div
      v-if="isInteracting"
      class="tray-interaction-overlay"
    ></div>

    <!-- En modo popout, el tray completo se colapsa. Solo se renderiza un
         chip flotante pequeno que permite volver a anclar las camaras.
         mousedown.stop: defensivo, evita que burbujee al root por si en el
         futuro onTrayMouseDown deja de salir temprano en modo popout. -->
    <div
      v-if="isPopout && cameras.length > 0"
      class="popout-reminder-chip"
      role="button"
      tabindex="0"
      @click="togglePopoutMode"
      @mousedown.stop
      @keydown.enter="togglePopoutMode"
      @keydown.space.prevent="togglePopoutMode"
    >
      <v-icon size="16" color="var(--kln-accent)">mdi-open-in-new</v-icon>
      <span class="popout-reminder-label">
        {{ cameras.length }} cámara{{ cameras.length === 1 ? '' : 's' }} en ventana independiente
      </span>
      <span class="popout-reminder-action">Traer de vuelta</span>
    </div>
    <!-- Resize handles (solo en modo floating) — 4 esquinas + 4 bordes -->
    <template v-if="isFloating">
      <div class="resize-handle resize-handle--n"  @mousedown.stop="_startResize('n',  $event)"></div>
      <div class="resize-handle resize-handle--e"  @mousedown.stop="_startResize('e',  $event)"></div>
      <div class="resize-handle resize-handle--s"  @mousedown.stop="_startResize('s',  $event)"></div>
      <div class="resize-handle resize-handle--w"  @mousedown.stop="_startResize('w',  $event)"></div>
      <div class="resize-handle resize-handle--nw" @mousedown.stop="_startResize('nw', $event)"></div>
      <div class="resize-handle resize-handle--ne" @mousedown.stop="_startResize('ne', $event)"></div>
      <div class="resize-handle resize-handle--sw" @mousedown.stop="_startResize('sw', $event)"></div>
      <div class="resize-handle resize-handle--se" @mousedown.stop="_startResize('se', $event)"></div>
    </template>
    <!-- Tray header — drag lo maneja el mousedown del root (onTrayMouseDown).
         En modo popout se oculta porque el tray se colapsa a un chip pequeno. -->
    <div
      v-if="!isPopout"
      class="tray-header"
      :class="{ 'tray-header--draggable': isFloating }"
    >
      <div class="tray-title">
        <v-icon small color="var(--kln-text-muted)" class="mr-1">mdi-television-play</v-icon>
        <span>Cámaras en vivo</span>
        <span class="tray-count">{{ cameras.length }} / 36</span>
        <span v-if="activePreset && !activePresetDirty" class="tray-preset-name">· {{ activePreset.name }}</span>
        <span v-else-if="activePreset && activePresetDirty" class="tray-preset-name tray-preset-name--mod">· {{ activePreset.name }} (modificado)</span>
        <span v-else-if="isTrayUnsaved" class="tray-preset-name tray-preset-name--mod">· Sin guardar</span>
      </div>
      <div class="tray-controls">
        <!-- El header solo se renderiza en docked/floating (v-if="!isPopout"
             en el div padre), asi que aqui los controles no necesitan chequear
             isPopout de nuevo. -->
        <v-btn-toggle
          :value="gridCols"
          mandatory
          dense
          class="layout-toggle"
          @change="$emit('cols-change', $event)"
        >
          <v-btn
            v-for="opt in colsOptions"
            :key="opt.value"
            :value="opt.value"
            x-small
            text
            v-auto-tooltip="opt.label"
          >
            {{ opt.value }}
          </v-btn>
        </v-btn-toggle>

        <v-menu v-model="presetMenuOpen" offset-y :close-on-content-click="false">
          <template #activator="{ on, attrs }">
            <v-btn icon x-small v-bind="attrs" v-on="on" v-auto-tooltip="'Presets'">
              <v-icon small>mdi-view-grid-plus</v-icon>
            </v-btn>
          </template>
          <v-list dense class="preset-menu">
            <v-subheader>PRESETS DE VIDEO WALL</v-subheader>
            <v-list-item v-if="!trayPresets.length" disabled>
              <v-list-item-title class="text--disabled">Sin presets guardados</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-for="p in trayPresets"
              :key="p.id"
              :class="{ 'preset-active': activePreset && activePreset.id === p.id }"
              @click="applyPresetAndClose(p.id)"
            >
              <v-list-item-icon class="mr-2">
                <v-icon small>{{ activePreset && activePreset.id === p.id ? 'mdi-check-circle' : 'mdi-grid' }}</v-icon>
              </v-list-item-icon>
              <v-list-item-title>{{ p.name }}</v-list-item-title>
              <v-btn icon x-small v-auto-tooltip="'Eliminar preset'" @click.stop="$emit('delete-preset', p.id)">
                <v-icon x-small>mdi-delete-outline</v-icon>
              </v-btn>
            </v-list-item>
            <v-divider />
            <!-- Actualizar el preset activo en sitio (solo si hay cambios sin guardar) -->
            <v-list-item v-if="activePreset && activePresetDirty" @click="onUpdatePreset">
              <v-list-item-icon class="mr-2">
                <v-icon small color="var(--kln-warning, #F39C12)">mdi-content-save-edit</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Guardar cambios en «{{ activePreset.name }}»</v-list-item-title>
            </v-list-item>
            <template v-if="savingPreset">
              <v-list-item class="preset-save-row">
                <v-text-field
                  v-model="newPresetName"
                  dense
                  autofocus
                  hide-details
                  placeholder="Nombre del preset"
                  class="preset-name-input"
                  @keyup.enter="confirmSavePreset"
                />
                <v-btn icon x-small v-auto-tooltip="'Guardar'" @click="confirmSavePreset">
                  <v-icon x-small>mdi-check</v-icon>
                </v-btn>
                <v-btn icon x-small v-auto-tooltip="'Cancelar'" @click="savingPreset = false">
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </v-list-item>
            </template>
            <v-list-item v-else @click="savingPreset = true; newPresetName = ''">
              <v-list-item-icon class="mr-2"><v-icon small>mdi-content-save-outline</v-icon></v-list-item-icon>
              <v-list-item-title>Guardar como nuevo preset…</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-btn
          icon
          x-small
          v-auto-tooltip="isFloating ? 'Anclar abajo' : 'Modo flotante'"
          :class="{ 'tray-btn-active': isFloating }"
          :disabled="isFullscreen"
          @click="toggleFloatingMode"
        >
          <v-icon small>{{ isFloating ? 'mdi-dock-bottom' : 'mdi-picture-in-picture-bottom-right' }}</v-icon>
        </v-btn>

        <v-btn
          icon
          x-small
          v-auto-tooltip="isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa (videowall)'"
          :disabled="isPopout"
          :class="{ 'tray-btn-active': isFullscreen }"
          @click="toggleFullscreenMode"
        >
          <v-icon small>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
        </v-btn>

        <v-btn
          icon
          x-small
          v-auto-tooltip="'Abrir en ventana independiente'"
          :disabled="isFullscreen"
          @click="togglePopoutMode"
        >
          <v-icon small>mdi-open-in-new</v-icon>
        </v-btn>

        <v-btn icon x-small v-auto-tooltip="'Quitar todas'" @click="$emit('clear-all')">
          <v-icon small>mdi-delete-sweep</v-icon>
        </v-btn>

        <v-btn icon x-small class="tray-close-btn" v-auto-tooltip="'Cerrar bandeja'" @click="$emit('close')">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Camera grid container (scroll vertical si hay mas camaras que slots).
         Se oculta en modo popout — el tray colapsa al chip superior. -->
    <div v-if="!isPopout" ref="gridContainer" class="tray-grid-container" :style="containerStyle">
      <div class="tray-grid" :style="gridStyle">
      <!-- Filled cells. Envoltorio con display:contents para aislar el diff
           de Vue 2 del v-for de las empty cells (de lo contrario, al quitar
           la primera cámara, Vue hace insertBefore sobre los iframes
           restantes y Chrome recarga el video). -->
      <div class="tray-grid__cameras">
      <div
        v-for="{ camera, pinned } in cells"
        :key="camera.id"
        class="camera-cell"
        :style="pinned ? cellPositionStyle(camera.id) : null"
        :class="{
          'camera-cell--collapsed': !!collapsedById[camera.id],
          'camera-cell--maximized': camera.id === maximizedId,
          'camera-cell--prewarm-hidden': !pinned,
          'camera-cell--dragging': camera.id === draggingId,
        }"
      >
        <!-- Wrapper con dimensiones calculadas via JS para letterboxing consistente.
             Cuando la cámara está colapsada NO renderizamos el iframe; en su lugar
             un placeholder visual mantiene el slot del grid. -->
        <div class="camera-aspect" :style="cellAspectStyle(camera.id)">
          <iframe
            v-if="camera.streamUrl && !collapsedById[camera.id]"
            :key="'cam-iframe-' + camera.id + '-' + (reloadKeys[camera.id] || 0)"
            :src="camera.streamUrl"
            class="camera-video"
            frameborder="0"
            allow="autoplay"
          ></iframe>
          <div v-else-if="collapsedById[camera.id]" class="camera-collapsed-placeholder">
            <v-icon color="rgba(255,255,255,0.35)" size="32">mdi-television-off</v-icon>
            <span class="camera-collapsed-label">Cámara minimizada</span>
          </div>
        </div>
        <!-- Header del modo maximizado (mismo look que el overlay anterior).
             position: absolute → va DESPUÉS del .camera-aspect para no
             reordenar el <iframe> (un reorden antes del iframe lo recargaría). -->
        <div
          v-if="camera.id === maximizedId"
          class="camera-maximized-header"
        >
          <div class="camera-maximized-title">
            <v-icon small color="rgba(255,255,255,0.7)" class="mr-2">mdi-television-play</v-icon>
            <span>{{ camera.name || 'Cámara' }}</span>
          </div>
          <div class="camera-maximized-actions">
            <v-btn icon small v-auto-tooltip="'Recargar cámara'" @click.stop="onCameraReload(camera)">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <v-btn icon small v-auto-tooltip="'Cerrar (ESC)'" @click.stop="closeMaximized">
              <v-icon>mdi-fullscreen-exit</v-icon>
            </v-btn>
          </div>
        </div>
        <!-- PTZ controls (same pattern as old CameraWindow.vue).
             Se ocultan cuando la cámara está colapsada para no quedar
             "flotando" sobre el placeholder. -->
        <div v-if="camera.isPtz && !collapsedById[camera.id]" class="ptz-controls">
          <div class="ptz-dpad">
            <div class="d-flex justify-center">
              <v-btn color="secondary" fab x-small dark @click="ptzMove('moveup', camera)"><v-icon>mdi-chevron-up</v-icon></v-btn>
            </div>
            <div class="d-flex justify-space-between">
              <v-btn color="secondary" fab x-small dark @click="ptzMove('moveleft', camera)"><v-icon>mdi-chevron-left</v-icon></v-btn>
              <v-btn color="secondary" fab x-small dark @click="ptzMove('moveright', camera)"><v-icon>mdi-chevron-right</v-icon></v-btn>
            </div>
            <div class="d-flex justify-center">
              <v-btn color="secondary" fab x-small dark @click="ptzMove('movedown', camera)"><v-icon>mdi-chevron-down</v-icon></v-btn>
            </div>
          </div>
          <div class="ptz-zoom">
            <v-btn color="secondary" fab x-small dark @click="ptzMove('zoomin', camera)"><v-icon>mdi-magnify-plus</v-icon></v-btn>
            <v-btn color="secondary" fab x-small dark @click="ptzMove('zoomout', camera)"><v-icon>mdi-magnify-minus</v-icon></v-btn>
          </div>
        </div>
        <div class="camera-overlay">
          <span
            v-if="!collapsedById[camera.id]"
            class="camera-drag-handle"
            v-auto-tooltip="'Arrastrar para mover'"
            @mousedown.stop.prevent="onCellDragStart(camera, $event)"
          >
            <v-icon x-small>mdi-drag</v-icon>
          </span>
          <span class="camera-name">{{ camera.name }}</span>
          <v-btn
            icon
            x-small
            class="camera-reload-btn"
            v-auto-tooltip="'Recargar cámara'"
            :disabled="!!collapsedById[camera.id]"
            @click.stop="onCameraReload(camera)"
          >
            <v-icon x-small>mdi-refresh</v-icon>
          </v-btn>
          <v-btn
            icon
            x-small
            class="camera-collapse-btn"
            v-auto-tooltip="collapsedById[camera.id] ? 'Restaurar video' : 'Minimizar (ocultar video)'"
            @click.stop="toggleCollapseCamera(camera)"
          >
            <v-icon x-small>{{ collapsedById[camera.id] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </v-btn>
          <v-btn
            icon
            x-small
            class="camera-focus-btn"
            v-auto-tooltip="'Ver en pantalla completa'"
            :disabled="!!collapsedById[camera.id]"
            @click.stop="onCameraFocus(camera)"
          >
            <v-icon x-small>mdi-fullscreen</v-icon>
          </v-btn>
          <v-btn
            v-if="!collapsedById[camera.id]"
            icon
            x-small
            class="camera-size-btn"
            v-auto-tooltip="'Tamaño en la cuadrícula'"
            @click.stop="openSizeMenu(camera.id, $event)"
          >
            <v-icon x-small>mdi-view-grid-plus-outline</v-icon>
          </v-btn>
          <v-btn
            icon
            x-small
            class="camera-remove-btn"
            v-auto-tooltip="'Quitar del tray'"
            @click.stop="$emit('remove-camera', camera.id)"
          >
            <v-icon x-small>mdi-close</v-icon>
          </v-btn>
        </div>
        <div v-if="loadingStates[camera.id] && !collapsedById[camera.id]" class="camera-loading">
          <v-progress-circular indeterminate size="24" color="var(--kln-accent)" />
        </div>
        <div v-if="errorStates[camera.id] && !collapsedById[camera.id]" class="camera-error">
          <v-icon small color="error">mdi-alert-circle</v-icon>
          <span>Sin señal</span>
        </div>
      </div>

      </div><!-- end tray-grid__cameras -->
      <!-- Empty cells to fill the grid (wrapper con display:contents, ver
           comentario arriba sobre el aislamiento del diff para prevenir el
           remount de iframes al quitar cámaras). -->
      <div class="tray-grid__empties">
      <div
        v-for="n in emptySlots"
        :key="'empty-' + n"
        class="camera-cell camera-cell--empty"
      >
        <v-icon color="var(--kln-border-dim)" size="32">mdi-cctv-off</v-icon>
      </div>
      </div><!-- end tray-grid__empties -->
      <!-- Resalte de la celda destino mientras se arrastra para mover -->
      <div
        v-if="dragTarget"
        class="drag-target-highlight"
        :style="dragTargetStyle"
      ></div>
    </div>
    </div>

    <!-- Selector visual de tamaño (único; se posiciona en el clic del botón de cada celda) -->
    <v-menu
      v-model="sizeMenuOpen"
      :position-x="sizeMenuX"
      :position-y="sizeMenuY"
      absolute
      offset-y
      :close-on-content-click="false"
    >
      <div class="size-picker" @mouseleave="sizePickerHover = { w: 0, h: 0 }">
        <div class="size-picker__caption">Tamaño en la cuadrícula</div>
        <div v-for="r in sizePickerRows" :key="'spr-' + r" class="size-picker__row">
          <div
            v-for="c in sizePickerCols"
            :key="'spc-' + r + '-' + c"
            class="size-picker__cell"
            :class="{ 'size-picker__cell--on': c <= sizePickerHover.w && r <= sizePickerHover.h }"
            @mouseover="sizePickerHover = { w: c, h: r }"
            @click="applySizeFromPicker(c, r)"
          ></div>
        </div>
        <div class="size-picker__label">{{ sizePickerHover.w || 1 }} × {{ sizePickerHover.h || 1 }}</div>
      </div>
    </v-menu>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import ApiService from '@/util/api.service'
import { packMosaic, pointToCell, clampSize, canPlace } from '@/components/map-v2/utils/mosaic-packer'

// Hls.js es ~511 KB (96% unused en boot según Lighthouse). Lo importamos
// dinámicamente solo cuando realmente hay que reproducir un stream HLS.
// La mayoría de cámaras son MJPEG/iframe y nunca activan este path —
// para esos usuarios el bundle de boot baja en ~170 KB transferidos.
// Cache del módulo para que el segundo stream no espere otro fetch.
let _hlsModulePromise = null
function loadHls() {
  if (!_hlsModulePromise) {
    _hlsModulePromise = import('hls.js').then(m => m.default)
  }
  return _hlsModulePromise
}

// ─── Floating mode constants ────────────────────────────────
// Dimensiones minimas absolutas del tray flotante (para no colapsar
// el contenido util — header + al menos 1 row de camaras).
const TRAY_MIN_W = 480
const TRAY_MIN_H = 220

// Fraccion maxima del viewport que el tray flotante puede ocupar
// (enforced por la math del resize, no por inline max-width/height).
const TRAY_MAX_VW = 0.95
const TRAY_MAX_VH = 0.80

// Cantidad minima visible del tray cuando se arrastra/resize cerca del borde
// (para que el usuario siempre pueda agarrarlo y traerlo de vuelta).
const MIN_VISIBLE_X = 120
const MIN_VISIBLE_Y = 40

// Dimensiones de los sidebars (hardcoded; coinciden con MapSidebar 280px
// y MapDrawer 300px). Si cambian alla, actualizar aqui tambien.
const LAYOUT_DIMS = {
  sidebarW: 280,
  drawerW: 300,
  margin: 12,  // margin desde el borde del viewport (overlay-sidebar left: 12px)
  gap: 16,     // separacion entre sidebar y tray
}

// ─── Popout window state compartido a nivel de modulo ──────
// La referencia a la ventana popout debe sobrevivir al unmount del componente
// (si el usuario minimiza el tray local clickeando "cerrar bandeja", el
// componente se destruye pero la ventana popout debe seguir viva). Al
// re-montar, el componente re-attachea el poll a la misma ventana.
let sharedPopoutWindow = null
let sharedPopoutPoll = null

export default {
  name: 'CameraTray',

  props: {
    cameras: {
      type: Array,
      default: () => [],
    },
    // Cámaras precalentadas (PMI abierto). Se montan OCULTAS (display:none) en
    // su celda final del grid para que el stream cargue en background; al fijar
    // solo se les quita el ocultamiento → aparecen al instante sin recargar.
    prewarmCameras: {
      type: Array,
      default: () => [],
    },
    grid: {
      type: Object,
      default: () => ({ cols: 4, tiles: [] }),
    },
  },

  data() {
    return {
      // Lista unificada y ESTABLE de celdas: fijadas + precalentadas juntas.
      // `pinned` controla solo la visibilidad (display); NUNCA se reordena, así
      // el <iframe> no se mueve y el stream no se recarga al fijar. Reconciliada
      // por watchers desde las props `cameras` (fijadas) y `prewarmCameras`.
      cells: [],
      hlsInstances: {},
      loadingStates: {},
      errorStates: {},
      containerWidth: 0, // ancho del grid, actualizado por ResizeObserver
      containerHeight: 0, // alto disponible del grid, actualizado por ResizeObserver
      // Flag que muestra un overlay full-viewport durante drag/resize para
      // interceptar mouse events sobre los iframes de camaras (que si no los
      // "tragan" y la interaccion queda stuck).
      isInteracting: false,
      colsOptions: [
        { value: 2, label: '2 columnas' },
        { value: 3, label: '3 columnas' },
        { value: 4, label: '4 columnas' },
        { value: 6, label: '6 columnas' },
        { value: 8, label: '8 columnas' },
        { value: 12, label: '12 columnas' },
      ],
      // Contador de reloads por cámara (indexado por camera.id).
      // Al incrementarlo forzamos re-mount del iframe via :key dinamico
      // para recargar el feed cuando se congela. NO vive en el store
      // porque es puro UI concern (no es una propiedad de dominio).
      reloadKeys: {},
      // Mapa cameraId → bool de cámaras "colapsadas" individualmente: la
      // celda mantiene su slot en el grid pero NO renderiza el iframe
      // (ahorra ancho de banda y CPU del browser). UI state, no persistido,
      // mismo patrón que reloadKeys. Para expandirla, toggle desde el
      // overlay (chevron).
      collapsedById: {},
      // id de la cámara maximizada (pantalla completa individual) o null.
      // El maximizar promueve la MISMA celda a position:fixed (CSS-promote)
      // en vez de montar un iframe nuevo en un overlay aparte, así el stream
      // NO se recarga. UI state, no persistido.
      maximizedId: null,
      // Estado del menú de presets: abierto/cerrado, modo guardado, nombre.
      presetMenuOpen: false,
      savingPreset: false,
      newPresetName: '',
      // Selector visual de tamaño (un solo menú posicionado en el clic).
      sizeMenuOpen: false,
      sizeMenuX: 0,
      sizeMenuY: 0,
      sizeMenuFor: null, // cameraId al que aplica el selector
      sizePickerHover: { w: 0, h: 0 },
      // Feedback del arrastre para mover: id de la celda en movimiento y
      // celda destino resaltada en vivo.
      draggingId: null,
      dragTarget: null, // { col, row, w, h } o null
    }
  },

  computed: {
    ...mapState('map-v2', {
      trayMode: state => state.tray.mode,
      trayFloatingPos: state => state.tray.floatingPos,
      trayFloatingSize: state => state.tray.floatingSize,
    }),
    ...mapGetters('map-v2', ['trayPresets', 'activePreset', 'activePresetDirty', 'isTrayUnsaved']),

    isFloating() {
      return this.trayMode === 'floating'
    },

    isPopout() {
      return this.trayMode === 'popout'
    },

    isFullscreen() {
      return this.trayMode === 'fullscreen'
    },

    // Estilos aplicados al root del tray:
    // - docked: sin override, el tray usa su layout normal en .zone-tray
    // - floating: position fixed + aparecia (borde, sombra). IMPORTANTE:
    //   left/top/width/height NO van aqui. Se aplican siempre via DOM directo
    //   desde el JS (_applyFloatingLayoutToDOM, _onDragMoveImpl, _onResizeMoveImpl).
    //   Si los pusiera en el computed, Vue los re-aplicaria en cada re-render
    //   (por ejemplo cuando isInteracting cambia), sobrescribiendo los
    //   updates manuales durante drag/resize -> bug: las esquinas NW/NE/SW
    //   parecerian "pegarse" al top-left porque Vue reverte left/top al
    //   valor viejo del store.
    rootStyle() {
      // Solo el modo floating usa estilos inline aqui. El modo fullscreen
      // se controla por CSS (clase .camera-tray--fullscreen con position:
      // fixed !important e inset: 0). NO agregues logica de fullscreen en
      // este computed — si lo haces, el CSS de .camera-tray--fullscreen
      // podria perder la batalla de especificidad contra los inline styles
      // (inline gana por default) y romper el videowall.
      if (!this.isFloating) return {}
      return {
        position: 'fixed',
        // z-index explícito: como position:fixed sin z-index queda en el grupo
        // "z-auto", los controles del mapa con z-index positivo (zoom z:6,
        // topbar z:11, etc.) pintaban ENCIMA del tray flotante. 1000 lo deja
        // sobre el mapa y sus paneles (≤50) pero debajo de las alertas SOS
        // (panel 2200 / banner 2500) y del videowall/cámara maximizada.
        zIndex: 1000,
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
        border: '1px solid var(--kln-border-accent)',
      }
    },

    gridCols() {
      return (this.grid && this.grid.cols) || 4
    },

    packed() {
      return packMosaic({ cols: this.gridCols, tiles: (this.grid && this.grid.tiles) || [] })
    },

    // Ancho real de una celda 1×1 (sin gaps).
    cellWidthPx() {
      if (!this.containerWidth) return 0
      const TRAY_PADDING = 4
      const GAP = 4
      const totalGaps = (this.gridCols - 1) * GAP
      return Math.max(0, (this.containerWidth - TRAY_PADDING - totalGaps) / this.gridCols)
    },

    // Alto de cada fila.
    // - DOCKED: la barra mide siempre el alto de UNA fila. La fila llena ese
    //   alto (una cámara) y las filas extra hacen scroll. No crece ni encoge.
    //   El video conserva su altura con franjas negras a los lados (letterbox
    //   vía cellAspectStyle).
    // - FULLSCREEN / FLOATING: usa una celda 16:9 (ancho×9/16) si todas las
    //   filas caben; si no, encoge para que quepan sin scroll (no baja de un
    //   mínimo usable; si ni así caben, permite scroll).
    rowHeight() {
      const w = this.cellWidthPx
      if (!w) return '150px'
      const ideal = (w * 9) / 16
      const ch = this.containerHeight
      if (!ch) return Math.round(ideal) + 'px'
      const PADDING = 4
      if (!this.isFullscreen && !this.isFloating) {
        // Docked: una fila llena la barra; el resto hace scroll.
        return Math.round(Math.max(0, ch - PADDING)) + 'px'
      }
      const GAP = 4
      const rows = Math.max(1, this.packed.rows)
      const avail = Math.max(0, ch - PADDING - (rows - 1) * GAP)
      const fitAll = avail / rows
      const MIN = 110
      const h = Math.max(MIN, Math.min(ideal, fitAll))
      return Math.round(h) + 'px'
    },

    gridStyle() {
      return {
        display: 'grid',
        gridTemplateColumns: `repeat(${this.gridCols}, 1fr)`,
        gridAutoRows: this.rowHeight,
        // Las filas mantienen su alto 16:9 y se empacan arriba: sin estirar
        // para llenar el viewport (en fullscreen) ni dejar gaps verticales.
        alignContent: 'start',
        gap: '4px',
        padding: '2px',
      }
    },

    containerStyle() {
      return {
        flex: '1 1 0',
        minHeight: 0,
        overflowY: 'auto',
        background: 'rgba(0, 0, 0, 0.3)',
      }
    },

    // Celdas vacías = celdas libres en la última fila usada del packer.
    emptySlots() {
      const total = this.gridCols * this.packed.rows
      let occupied = 0
      const pos = this.packed.positions
      Object.keys(pos).forEach((id) => { occupied += pos[id].w * pos[id].h })
      const free = total - occupied
      return free > 0 ? free : 0
    },

    // Columnas del selector visual de tamaño: hasta 4, acotado por el grid.
    sizePickerCols() {
      return Math.min(4, this.gridCols)
    },

    // Filas del selector visual de tamaño (alto máximo elegible).
    sizePickerRows() {
      return 4
    },

    // Estilo del resalte de celda destino durante el arrastre.
    dragTargetStyle() {
      const t = this.dragTarget
      if (!t) return {}
      return {
        gridColumn: `${t.col} / span ${t.w}`,
        gridRow: `${t.row} / span ${t.h}`,
      }
    },
  },

  watch: {
    cameras: {
      handler(newCams, oldCams) {
        // Reconciliar la lista unificada `cells` (fijadas + precalentadas).
        // Al fijar, la cámara entra a `cameras` → su celda voltea pinned=true
        // in-place (sin reordenar) → aparece al instante sin recargar.
        this._reconcileCells()

        const oldIds = new Set((oldCams || []).map(c => c.id))
        const newIds = new Set(newCams.map(c => c.id))

        // Destroy removed cameras
        oldIds.forEach(id => {
          if (!newIds.has(id)) {
            this._destroyHls(id)
          }
        })

        // Si la cámara maximizada se quitó del tray, salir del modo maximizado.
        if (this.maximizedId != null && !newIds.has(this.maximizedId)) {
          this.maximizedId = null
        }

        // Init new cameras after DOM update
        this.$nextTick(() => {
          newCams.forEach(cam => {
            if (!oldIds.has(cam.id)) {
              this._initHls(cam)
            }
          })
        })

        // Si hay una ventana popout abierta, re-construir su contenido
        // cuando cambian las camaras (pinear/despinear desde el mapa).
        if (this.isPopout && sharedPopoutWindow && !sharedPopoutWindow.closed) {
          this._buildPopoutContent(sharedPopoutWindow)
        }
      },
      deep: false,
    },

    // Cambian las precalentadas (abrir/cambiar/cerrar PMI) → reconciliar.
    // Las precalentadas-no-fijadas que ya no están se quitan (iframe se
    // desmonta); las nuevas se agregan ocultas. Las fijadas no se tocan.
    prewarmCameras() {
      this._reconcileCells()
    },

    grid: {
      handler() {
        // El set de cámaras NO cambia con cols/move/resize (eso lo maneja el
        // watcher de `cameras`). Actualizamos solo las POSICIONES del popout in
        // situ — recrear el DOM remontaría todos los iframes y cortaría los
        // streams del videowall externo.
        if (this.isPopout && sharedPopoutWindow && !sharedPopoutWindow.closed) {
          this._syncPopoutLayout(sharedPopoutWindow)
        }
      },
      deep: true,
    },

    // Al cerrar el menú de presets (incl. clic afuera), resetear el modo de
    // guardado para que al reabrir no aparezca el input con texto residual.
    presetMenuOpen(open) {
      if (!open) {
        this.savingPreset = false
        this.newPresetName = ''
      }
    },

    isFullscreen(val) {
      if (val) {
        // Montamos listener en fase burbuja. El modo maximizado (cámara
        // individual) se registra en fase captura con stopImmediatePropagation,
        // así que si ambos están activos el ESC cierra primero la maximizada.
        // Como defensa extra, el handler aquí también chequea maximizedId.
        document.addEventListener('keydown', this._onFullscreenKeydown)
      } else {
        document.removeEventListener('keydown', this._onFullscreenKeydown)
      }
      // Nota: si el componente se destruye mientras isFullscreen === true,
      // este watcher NO dispara. El beforeDestroy llama removeEventListener
      // incondicionalmente para cubrir ese caso.
    },

    maximizedId(val) {
      // Listener de ESC en fase CAPTURA (true) para cerrar la cámara
      // maximizada antes que el ESC del videowall. this._onMaximizedKeydown
      // es referencia estable (created), así addEventListener es idempotente
      // y removeEventListener la encuentra.
      if (val != null) {
        document.addEventListener('keydown', this._onMaximizedKeydown, true)
        // NO remontar el iframe al maximizar: el maximizado es CSS-promote
        // deliberadamente SIN recarga (instantáneo, misma política que el
        // maximizar de CameraStream en el drawer). El remount "para subir
        // resolución" (a62a52a1) producía pantalla negra de varios segundos
        // mientras el player renegociaba el stream — el operador prefiere
        // instantaneidad; el upscale del bitmap es aceptable.
      } else {
        document.removeEventListener('keydown', this._onMaximizedKeydown, true)
      }
    },

    // Re-init de los iframes cuando el tray CRECE de tamaño (debounced). El
    // player embebido fija su resolución al montar, así que al agrandar la
    // celda con CSS la imagen queda borrosa hasta remontar. Solo en
    // crecimiento >20% (encoger ya se ve nítido → sin parpadeo innecesario).
    containerWidth(w) {
      if (!w) return
      if (!this._lastTrayWidth) { this._lastTrayWidth = w; return }
      if (this._trayResizeTimer) clearTimeout(this._trayResizeTimer)
      this._trayResizeTimer = setTimeout(() => {
        this._trayResizeTimer = null
        const cur = this.containerWidth
        if (!cur) return
        if (cur > this._lastTrayWidth * 1.2) {
          this._lastTrayWidth = cur
          ;(this.cells || []).forEach((c) => {
            const id = c && c.camera && c.camera.id
            // La celda maximizada NO se remonta (está en CSS-promote fixed;
            // recargarla la pondría en negro en plena pantalla completa).
            if (id && !this.collapsedById[id] && id !== this.maximizedId) {
              this.$set(this.reloadKeys, id, (this.reloadKeys[id] || 0) + 1)
            }
          })
        } else if (cur < this._lastTrayWidth) {
          this._lastTrayWidth = cur
        }
      }, 500)
    },
  },

  created() {
    // Bindear los handlers de drag/resize UNA SOLA VEZ. Si los bindeo dentro
    // de _startResize/onTrayMouseDown, cada interaccion genera una funcion
    // nueva y si por cualquier motivo no se llega al mouseup (focus perdido,
    // tab switch, iframe tragando el evento), el listener previo queda
    // huerfano — imposible de remover despues porque la referencia cambio.
    this._onDragMove = this._onDragMoveImpl.bind(this)
    this._onDragEnd = this._onDragEndImpl.bind(this)
    this._onResizeMove = this._onResizeMoveImpl.bind(this)
    this._onResizeEnd = this._onResizeEndImpl.bind(this)
    this._onWindowResizeFloating = this._onWindowResizeFloatingImpl.bind(this)
    // Estado interno para el workaround del z-index del parent
    this._parentElevated = false
    this._originalParentZIndex = ''
    // Estado del drag/resize (null cuando no hay interaccion activa)
    this._dragOffset = null
    this._resizeState = null
    // Estado no reactivo del re-init por crecimiento del tray.
    this._lastTrayWidth = 0
    this._trayResizeTimer = null
    // Nota: el estado del popout (sharedPopoutWindow, sharedPopoutPoll)
    // vive a nivel de modulo, no de instancia — sobrevive al unmount
    // para que el usuario pueda minimizar el tray local sin perder la
    // ventana popout.
    this._onFullscreenKeydown = this._onFullscreenKeydownImpl.bind(this)
    // Referencia ESTABLE para el ESC del modo maximizado (cámara individual).
    // Se registra en fase CAPTURA con stopImmediatePropagation para ganarle
    // al ESC del videowall (fase burbuja): así ESC cierra primero la cámara
    // maximizada y deja el fullscreen del tray intacto.
    this._onMaximizedKeydown = this._onMaximizedKeydownImpl.bind(this)
    // Pre-bind de handlers de drag de celda (mover tiles).
    this._onCellDragMove = this._onCellDragMoveImpl.bind(this)
    this._onCellDragEnd = this._onCellDragEndImpl.bind(this)
  },

  mounted() {
    // Poblar `cells` al primer render desde las props actuales (fijadas +
    // precalentadas) antes de cualquier cambio que dispare los watchers.
    this._reconcileCells()
    // Gestion del modo popout al montar:
    // 1. Si hay una ventana popout viva en el state compartido, re-attachear
    //    el poll (el componente se destruyo mientras estaba minimizado y
    //    debemos seguir detectando cuando se cierra).
    // 2. Si el store dice popout pero no hay ventana (p.ej. tras un reload),
    //    resetear a docked para evitar estado inconsistente.
    // 3. Si no hay nada popout, ignorar.
    if (this.isPopout) {
      if (sharedPopoutWindow && !sharedPopoutWindow.closed) {
        this._startPopoutPoll()
      } else {
        sharedPopoutWindow = null
        this.$store.commit('map-v2/SET_TRAY_MODE', 'docked')
      }
    }
    this.$nextTick(() => {
      this.cameras.forEach(cam => this._initHls(cam))
      // Si el tray viene persistido en floating mode, clampear al viewport
      // actual (puede haber sido guardado en uno mas grande), elevar el
      // z-index del padre, y aplicar la geometria al DOM.
      if (this.isFloating) {
        this._clampFloatingLayoutToViewport()
        this._elevateParentZIndex()
        this._applyFloatingLayoutToDOM()
      }
    })
    this._setupResizeObserver()
    // Escuchar window.resize para re-clampear cuando la ventana cambie
    // (aplica solo en modo flotante; el handler chequea isFloating adentro)
    window.addEventListener('resize', this._onWindowResizeFloating)
    // Si el tray se hidrata en modo fullscreen (persistido en localStorage),
    // hacemos dos cosas manualmente porque el watcher y el toggle NO corren:
    // 1. Registrar el listener ESC (el watcher solo corre en cambios).
    // 2. Elevar el z-index del parent (.zone-tray crea stacking context
    //    con z-index 5 y sin elevar, los sidebars z-10 tapan el fullscreen).
    if (this.isFullscreen) {
      document.addEventListener('keydown', this._onFullscreenKeydown)
      this._elevateParentZIndex()
    }
  },

  beforeDestroy() {
    // Limpiar listener de ESC del fullscreen si quedó registrado
    document.removeEventListener('keydown', this._onFullscreenKeydown)
    // Limpiar listener de ESC del modo maximizado (fase captura)
    document.removeEventListener('keydown', this._onMaximizedKeydown, true)
    // Restaurar z-index del parent si quedo elevado (fullscreen activo)
    this._restoreParentZIndex()
    this._destroyAll()
    this._teardownResizeObserver()
    // Limpiar listeners de drag si el usuario cierra el tray mid-drag
    if (this._dragOffset) {
      document.removeEventListener('mousemove', this._onDragMove)
      document.removeEventListener('mouseup', this._onDragEnd)
      this._dragOffset = null
    }
    // Limpiar listeners de resize si el componente se destruye mid-resize
    if (this._resizeState) {
      document.removeEventListener('mousemove', this._onResizeMove)
      document.removeEventListener('mouseup', this._onResizeEnd)
      this._resizeState = null
    }
    // Limpiar listeners de drag de CELDA si se destruye el tray mid-interacción
    if (this._dragTileId) {
      document.removeEventListener('mousemove', this._onCellDragMove)
      document.removeEventListener('mouseup', this._onCellDragEnd)
      this._dragTileId = null
      this.draggingId = null
      this.dragTarget = null
    }
    // Restaurar cursor del body por si habiamos seteado uno durante la interaccion
    document.body.style.cursor = ''
    // Quitar el listener del window.resize
    window.removeEventListener('resize', this._onWindowResizeFloating)
    // Limpiar SOLO el poll del popout. NO cerrar la ventana — si el usuario
    // minimiza el tray local (panels.tray=false), la ventana popout debe
    // seguir viva y cuando vuelva a abrir el tray, el componente re-attacha.
    // sharedPopoutWindow persiste porque es modulo-level, no instance.
    if (sharedPopoutPoll) {
      window.clearInterval(sharedPopoutPoll)
      sharedPopoutPoll = null
    }
    // Restaurar z-index del padre si el componente se destruye en modo floating
    this._restoreParentZIndex()
  },

  methods: {
    // Estilo de posición CSS Grid de una celda, desde el packer.
    // Prewarm (no pinned) → no está en el packer → sin posición (queda hidden).
    cellPositionStyle(cameraId) {
      const p = this.packed.positions[cameraId]
      if (!p) return {}
      return {
        gridColumn: `${p.col} / span ${p.w}`,
        gridRow: `${p.row} / span ${p.h}`,
      }
    },

    // Wrapper 16:9 con letterboxing, dimensionado según el span del tile.
    cellAspectStyle(cameraId) {
      const p = this.packed.positions[cameraId]
      const w = p ? p.w : 1
      const h = p ? p.h : 1
      const GAP = 4
      const cellW = this.cellWidthPx
      const cellH = parseFloat(this.rowHeight) || 0
      if (!cellW) return { width: '100%', height: '100%', position: 'relative' }
      const totalW = cellW * w + GAP * (w - 1)
      const totalH = cellH * h + GAP * (h - 1)
      const targetAspect = 16 / 9
      let ww
      let hh
      if (totalW / totalH > targetAspect) {
        hh = totalH
        ww = hh * targetAspect
      } else {
        ww = totalW
        hh = ww / targetAspect
      }
      return { width: ww + 'px', height: hh + 'px', position: 'relative' }
    },

    // ─── Drag/move de tile ──────────────────────────────────────
    _gridGeom() {
      return {
        cellW: this.cellWidthPx,
        cellH: parseFloat(this.rowHeight) || 0,
        gap: 4,
        cols: this.gridCols,
      }
    },

    // Convierte el evento de mouse a la celda destino del grid (scroll + padding).
    _pointerToCell(e) {
      const cont = this.$refs.gridContainer
      if (!cont) return null
      const rect = cont.getBoundingClientRect()
      const x = e.clientX - rect.left + cont.scrollLeft - 2 // -padding
      const y = e.clientY - rect.top + cont.scrollTop - 2
      return pointToCell(x, y, this._gridGeom())
    },

    onCellDragStart(camera) {
      if (!this.$refs.gridContainer) return
      this._dragTileId = camera.id
      this.draggingId = camera.id
      const p = this.packed.positions[camera.id] || { w: 1, h: 1 }
      this._dragTileSize = { w: p.w, h: p.h }
      this.isInteracting = true
      document.body.style.cursor = 'grabbing'
      document.addEventListener('mousemove', this._onCellDragMove)
      document.addEventListener('mouseup', this._onCellDragEnd)
    },

    _onCellDragMoveImpl(e) {
      // Feedback en vivo: resalta la celda destino donde caería la cámara.
      if (!this._dragTileId) return
      const cell = this._pointerToCell(e)
      if (!cell) return
      const size = this._dragTileSize || { w: 1, h: 1 }
      const col = Math.max(1, Math.min(this.gridCols - size.w + 1, cell.col))
      this.dragTarget = {
        col, row: cell.row, w: size.w, h: size.h,
      }
    },

    _onCellDragEndImpl(e) {
      const id = this._dragTileId
      this._dragTileId = null
      this.draggingId = null
      this.dragTarget = null
      this.isInteracting = false
      document.body.style.cursor = ''
      document.removeEventListener('mousemove', this._onCellDragMove)
      document.removeEventListener('mouseup', this._onCellDragEnd)
      if (!id) return
      const target = this._pointerToCell(e)
      if (!target) return
      this._commitTileMove(id, target)
    },

    // Rects ocupados por las OTRAS celdas ancladas, usando sus posiciones REALES
    // del packer (ya clampadas a las columnas actuales y reubicadas si estaban
    // demoted). Las flotantes NO ocupan: ceden y se reacomodan alrededor.
    _pinnedOccupied(excludeId) {
      const positions = this.packed.positions
      const tiles = (this.grid && this.grid.tiles) || []
      const occupied = []
      tiles.forEach((t) => {
        if (t.id !== excludeId && t.pinned && positions[t.id]) occupied.push(positions[t.id])
      })
      return occupied
    },

    // Ancla el tile en la celda destino (snap). Si colisiona con otra anclada,
    // lo deja flotante para que el packer lo reacomode.
    _commitTileMove(id, target) {
      const tiles = ((this.grid && this.grid.tiles) || []).map((t) => ({ ...t }))
      const tile = tiles.find((t) => t.id === id)
      if (!tile) return
      const size = clampSize(tile.w, tile.h, this.gridCols)
      const occupied = this._pinnedOccupied(id)
      const col = Math.max(1, Math.min(this.gridCols - size.w + 1, target.col))
      // Clamp de fila: soltar fuera del grid no debe anclar en una fila lejana
      // (evita generar cientos de celdas vacías). Máx = una fila después de la
      // última usada.
      const maxRow = Math.max(1, this.packed.rows) + 1
      const row = Math.max(1, Math.min(maxRow, target.row))
      const rect = {
        col, row, w: size.w, h: size.h,
      }
      if (canPlace(rect, occupied, this.gridCols)) {
        tile.pinned = { col, row }
      } else {
        delete tile.pinned
      }
      this.$emit('tiles-change', tiles)
    },

    // ─── Selector visual de tamaño ──────────────────────────────
    openSizeMenu(cameraId, e) {
      this.sizeMenuFor = cameraId
      this.sizeMenuX = e.clientX
      this.sizeMenuY = e.clientY
      this.sizePickerHover = { w: 0, h: 0 }
      this.sizeMenuOpen = true
    },

    applySizeFromPicker(w, h) {
      const id = this.sizeMenuFor
      this.sizeMenuOpen = false
      if (id) this.setTileSize(id, w, h)
    },

    // Fija el tamaño de un tile (w×h) desde el selector. 1×1 = vuelve a flotar.
    // Si pisa otra anclada o se sale del grid, recorta hasta que quepa.
    setTileSize(id, w, h) {
      const tiles = ((this.grid && this.grid.tiles) || []).map((t) => ({ ...t }))
      const tile = tiles.find((t) => t.id === id)
      if (!tile) return
      const size = clampSize(w, h, this.gridCols)
      const curW = tile.w || 1
      const curH = tile.h || 1
      if (size.w === 1 && size.h === 1) {
        // No-op si ya está 1×1 flotante: no remontar el iframe (cortaría el stream).
        if (curW === 1 && curH === 1 && !tile.pinned) return
        delete tile.pinned
        tile.w = 1
        tile.h = 1
        this.$emit('tiles-change', tiles)
        this._bumpReload(id)
        return
      }
      // No-op si el tamaño no cambia y ya está anclado: evita remount/corte de stream.
      if (size.w === curW && size.h === curH && tile.pinned) return
      const base = this.packed.positions[id] || { col: 1, row: 1 }
      const occupied = this._pinnedOccupied(id)
      let col = Math.min(base.col, this.gridCols - size.w + 1)
      if (col < 1) col = 1
      let ww = size.w
      let hh = size.h
      const fits = () => canPlace({
        col, row: base.row, w: ww, h: hh,
      }, occupied, this.gridCols)
      while (ww > 1 && !fits()) ww -= 1
      while (hh > 1 && !fits()) hh -= 1
      tile.w = ww
      tile.h = hh
      const baseRect = {
        col, row: base.row, w: ww, h: hh,
      }
      if (canPlace(baseRect, occupied, this.gridCols)) {
        tile.pinned = { col, row: base.row }
      } else {
        delete tile.pinned
      }
      this.$emit('tiles-change', tiles)
      this._bumpReload(id)
    },

    // Re-monta el iframe de una celda (sube resolución tras cambiar de tamaño).
    _bumpReload(id) {
      const current = this.reloadKeys[id] || 0
      this.$set(this.reloadKeys, id, current + 1)
    },

    confirmSavePreset() {
      if (!this.newPresetName.trim()) return
      this.$emit('save-preset', this.newPresetName.trim())
      this.savingPreset = false
      this.presetMenuOpen = false
      this.newPresetName = ''
    },

    applyPresetAndClose(id) {
      this.$emit('apply-preset', id)
      this.presetMenuOpen = false
    },

    onUpdatePreset() {
      this.$emit('update-preset')
      this.presetMenuOpen = false
    },

    ptzMove: function(action, camera) {
      if (!camera.uniqueId || !camera.devId) return
      ApiService({
        url: '/cameras/ptz/' + action,
        method: 'get',
        params: { uniqueid: camera.uniqueId, camname: camera.devId },
      }).catch(function(err) { console.error('[PTZ]', action, err) })
    },

    async _initHls(camera) {
      const videoEl = this.$refs['video-' + camera.id]
      if (!videoEl) return

      // Clean up any existing instance for this id
      this._destroyHls(camera.id)

      this.$set(this.loadingStates, camera.id, true)
      this.$set(this.errorStates, camera.id, false)

      const url = camera.streamUrl

      // Carga Hls dinámicamente la primera vez que se reproduce un stream
      // HLS. Las siguientes invocaciones reusan el módulo cacheado.
      const Hls = await loadHls()

      // Si el componente se desmonto durante el await, abortar.
      if (!this.$refs['video-' + camera.id]) return

      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          backBufferLength: 30,
        })

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            this.$set(this.loadingStates, camera.id, false)
            this.$set(this.errorStates, camera.id, true)
          }
        })

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          this.$set(this.loadingStates, camera.id, false)
          videoEl.play().catch(() => {})
        })

        hls.loadSource(url)
        hls.attachMedia(videoEl)
        this.hlsInstances[camera.id] = hls

      } else if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS (Safari)
        videoEl.src = url
        videoEl.addEventListener('loadedmetadata', () => {
          this.$set(this.loadingStates, camera.id, false)
          videoEl.play().catch(() => {})
        }, { once: true })
        videoEl.addEventListener('error', () => {
          this.$set(this.loadingStates, camera.id, false)
          this.$set(this.errorStates, camera.id, true)
        }, { once: true })

      } else {
        this.$set(this.loadingStates, camera.id, false)
        this.$set(this.errorStates, camera.id, true)
      }
    },

    _destroyHls(cameraId) {
      const hls = this.hlsInstances[cameraId]
      if (hls) {
        hls.destroy()
        delete this.hlsInstances[cameraId]
      }
      this.$delete(this.loadingStates, cameraId)
      this.$delete(this.errorStates, cameraId)
    },

    _destroyAll() {
      Object.keys(this.hlsInstances).forEach(id => {
        this.hlsInstances[id].destroy()
      })
      this.hlsInstances = {}
      this.loadingStates = {}
      this.errorStates = {}
    },

    _setupResizeObserver() {
      const el = this.$refs.gridContainer
      if (!el) return
      // Inicializar ancho y alto (primer frame)
      this.containerWidth = el.clientWidth
      this.containerHeight = el.clientHeight
      // Observer para trackear cambios (resize de ventana, toggle sidebar, etc.)
      if (typeof ResizeObserver !== 'undefined') {
        this._resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            this.containerWidth = entry.contentRect.width
            this.containerHeight = entry.contentRect.height
          }
        })
        this._resizeObserver.observe(el)
      } else {
        // Fallback: solo escuchar el resize de la ventana
        this._onWindowResize = () => {
          if (this.$refs.gridContainer) {
            this.containerWidth = this.$refs.gridContainer.clientWidth
            this.containerHeight = this.$refs.gridContainer.clientHeight
          }
        }
        window.addEventListener('resize', this._onWindowResize)
      }
    },

    _teardownResizeObserver() {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect()
        this._resizeObserver = null
      }
      if (this._onWindowResize) {
        window.removeEventListener('resize', this._onWindowResize)
        this._onWindowResize = null
      }
      if (this._trayResizeTimer) {
        clearTimeout(this._trayResizeTimer)
        this._trayResizeTimer = null
      }
    },

    // ─── Floating mode ──────────────────────────────────────

    // Calcula la geometria inicial para el modo flotante.
    // Ancho = viewport - sidebars - margins -> el tray cabe "entre" los sidebars
    // Posicion X = despues del sidebar
    // Posicion Y = alineado con el bottom de los sidebars (12px del fondo)
    _computeFloatingGeometry() {
      const occupied = LAYOUT_DIMS.sidebarW + LAYOUT_DIMS.drawerW
        + (LAYOUT_DIMS.margin * 2) + (LAYOUT_DIMS.gap * 2)
      const maxW = Math.floor(window.innerWidth * TRAY_MAX_VW)
      const width = Math.min(maxW, Math.max(TRAY_MIN_W, window.innerWidth - occupied))
      const maxH = Math.floor(window.innerHeight * TRAY_MAX_VH)
      const height = Math.min(maxH, this.packed.rows >= 2 ? 360 : 220)
      const x = LAYOUT_DIMS.margin + LAYOUT_DIMS.sidebarW + LAYOUT_DIMS.gap
      // Bottom alineado con los sidebars (bottom: 12px)
      const y = Math.max(12, window.innerHeight - LAYOUT_DIMS.margin - height)
      return { x, y, width, height }
    },

    // Clampa posicion + tamanio al viewport actual para que no queden valores
    // imposibles de usar (tray fuera de la pantalla o mas grande que el viewport).
    // Se usa en mount (para restaurar estado persistido que fue guardado en
    // un viewport mas grande) y en resize de ventana.
    _clampFloatingLayoutToViewport() {
      const size = this.trayFloatingSize || { w: null, h: null }
      const pos = this.trayFloatingPos || { x: 100, y: 100 }
      const maxW = Math.floor(window.innerWidth * TRAY_MAX_VW)
      const maxH = Math.floor(window.innerHeight * TRAY_MAX_VH)
      let w = size.w
      let h = size.h
      if (w && w > maxW) w = maxW
      if (h && h > maxH) h = maxH
      if (w && w < TRAY_MIN_W) w = TRAY_MIN_W
      if (h && h < TRAY_MIN_H) h = TRAY_MIN_H
      // Clamp posicion asumiendo el tamanio actualizado
      const effW = w || TRAY_MIN_W
      const effH = h || TRAY_MIN_H
      const maxX = Math.max(0, window.innerWidth - MIN_VISIBLE_X)
      const maxY = Math.max(0, window.innerHeight - MIN_VISIBLE_Y)
      let x = Math.max(0, Math.min(maxX, pos.x))
      let y = Math.max(0, Math.min(maxY, pos.y))
      // Si el tamanio cambio, persistir
      if (w !== size.w || h !== size.h) {
        this.$store.commit('map-v2/SET_TRAY_FLOATING_SIZE', { w, h })
      }
      if (x !== pos.x || y !== pos.y) {
        this.$store.commit('map-v2/SET_TRAY_FLOATING_POS', { x, y })
      }
      return { x, y, w: effW, h: effH }
    },

    // Aplica position + size al DOM directamente desde el store.
    // Es el UNICO lugar que aplica left/top/width/height durante el render
    // normal (fuera de drag/resize). Evita que Vue reaplique esos valores
    // via el :style computed y pisotee los updates manuales del drag/resize.
    _applyFloatingLayoutToDOM() {
      const root = this.$refs.root
      if (!root) return
      const pos = this.trayFloatingPos || { x: 100, y: 100 }
      root.style.left = pos.x + 'px'
      root.style.top = pos.y + 'px'
      const size = this.trayFloatingSize || { w: null, h: null }
      if (size.w && size.h) {
        root.style.width = size.w + 'px'
        root.style.height = size.h + 'px'
      } else {
        // Primera entrada a floating sin size persistido — calcular y persistir
        const geo = this._computeFloatingGeometry()
        root.style.width = geo.width + 'px'
        root.style.height = geo.height + 'px'
        this.$store.commit('map-v2/SET_TRAY_FLOATING_SIZE', { w: geo.width, h: geo.height })
      }
    },

    // Cuando la ventana cambia de tamanio mientras el tray esta flotante,
    // clampear posicion y tamanio para que no queden fuera del viewport.
    _onWindowResizeFloatingImpl() {
      if (!this.isFloating) return
      // Ignorar durante drag/resize activo para no pelear con el usuario
      if (this._dragOffset || this._resizeState) return
      this._clampFloatingLayoutToViewport()
      // Re-aplicar el resultado ya clampeado al DOM
      this._applyFloatingLayoutToDOM()
    },

    toggleFloatingMode() {
      // Cancelar drag/resize en curso antes de cambiar de modo.
      // Si no, el listener mousemove sigue activo y puede mover el tray
      // en docked (setting inline left/top en el elemento del modo docked).
      if (this._dragOffset) {
        document.removeEventListener('mousemove', this._onDragMove)
        document.removeEventListener('mouseup', this._onDragEnd)
        this._dragOffset = null
      }
      if (this._resizeState) {
        document.removeEventListener('mousemove', this._onResizeMove)
        document.removeEventListener('mouseup', this._onResizeEnd)
        this._resizeState = null
      }
      this.isInteracting = false
      document.body.style.cursor = ''

      if (this.isFloating) {
        // Salir de floating: primero commit del mode para que Vue re-renderice
        // con los estilos docked, luego limpiar los inline styles. Haciendolo
        // al reves genera un flicker de 1 frame con el tray en su tamanio
        // custom pero ya con estilos de docked.
        this.$store.commit('map-v2/SET_TRAY_MODE', 'docked')
        this.$nextTick(() => {
          const root = this.$refs.root
          if (root) {
            // Limpiar TODOS los inline positioning que pusimos durante floating
            root.style.width = ''
            root.style.height = ''
            root.style.left = ''
            root.style.top = ''
          }
          this._restoreParentZIndex()
        })
      } else {
        // Entrar a floating: calcular geometria, commitear al store,
        // y luego aplicar layout al DOM directamente via _applyFloatingLayoutToDOM.
        const geo = this._computeFloatingGeometry()
        this.$store.commit('map-v2/SET_TRAY_FLOATING_POS', { x: geo.x, y: geo.y })
        this.$store.commit('map-v2/SET_TRAY_FLOATING_SIZE', { w: geo.width, h: geo.height })
        this._elevateParentZIndex()
        this.$store.commit('map-v2/SET_TRAY_MODE', 'floating')
        this.$nextTick(() => {
          this._applyFloatingLayoutToDOM()
        })
      }
    },

    toggleFullscreenMode() {
      // Si estamos en floating, limpiar estado antes del switch
      // (mismo patron que toggleFloatingMode al salir)
      if (this.isFloating) {
        if (this._dragOffset) {
          document.removeEventListener('mousemove', this._onDragMove)
          document.removeEventListener('mouseup', this._onDragEnd)
          this._dragOffset = null
        }
        if (this._resizeState) {
          document.removeEventListener('mousemove', this._onResizeMove)
          document.removeEventListener('mouseup', this._onResizeEnd)
          this._resizeState = null
        }
        this.isInteracting = false
        document.body.style.cursor = ''
        if (this.$refs.root) {
          this.$refs.root.style.width = ''
          this.$refs.root.style.height = ''
          this.$refs.root.style.left = ''
          this.$refs.root.style.top = ''
        }
        this._restoreParentZIndex()
      }

      if (this.isFullscreen) {
        // Salir del fullscreen: necesitamos saber a que modo volvemos
        // ANTES del dispatch para decidir si re-aplicamos layout floating
        // o restauramos el z-index del parent.
        //
        // Bug que esto resuelve: si el usuario estaba en floating y entro
        // a fullscreen, al salir el store pone mode: 'floating' pero los
        // inline styles (left/top/width/height) del floating fueron
        // limpiados al entrar a fullscreen. Sin re-aplicarlos el tray
        // renderiza en 0,0 sin dimensiones.
        const previousMode = this.$store.state['map-v2'].tray.previousMode
        this.$store.dispatch('map-v2/exitTrayFullscreen')
        this.$nextTick(() => {
          if (previousMode === 'floating') {
            // El parent ya esta elevado (de cuando entramos a fullscreen),
            // floating tambien lo necesita — lo dejamos asi. Re-aplicamos
            // el layout floating al DOM para restaurar left/top/width/height.
            this._applyFloatingLayoutToDOM()
          } else {
            // Volvemos a docked: restaurar el z-index normal del parent.
            this._restoreParentZIndex()
          }
        })
      } else {
        // Entrar a fullscreen: .zone-tray tiene z-index: 5 y crea un
        // stacking context, asi que el z-index: 9000 del tray fullscreen
        // solo compite DENTRO de .zone-tray. A nivel pagina, .zone-tray (5)
        // pierde contra los sidebars (10). Elevamos el parent para que
        // el fullscreen quede encima de todo. Al salir, restauramos.
        this._elevateParentZIndex()
        this.$store.dispatch('map-v2/enterTrayFullscreen')
      }
    },

    _onMaximizedKeydownImpl(e) {
      if (e.key !== 'Escape') return
      if (this.maximizedId == null) return
      // stopImmediatePropagation: impide que el ESC del videowall (fase
      // burbuja) también dispare. Así ESC cierra primero la cámara
      // maximizada y deja el fullscreen del tray intacto.
      e.stopImmediatePropagation()
      e.preventDefault()
      this.closeMaximized()
    },

    _onFullscreenKeydownImpl(e) {
      if (e.key !== 'Escape') return
      // Defense in depth: si hay una camara maximizada, no cerramos el
      // fullscreen del tray — el handler del modo maximizado (fase captura)
      // ya detuvo la propagacion, pero este check es belt-and-suspenders.
      if (this.maximizedId != null) return
      e.preventDefault()
      e.stopPropagation()
      // Reusa toggleFullscreenMode en vez de dispatch directo para que
      // ambos paths (boton del header y ESC) compartan la logica de
      // restauracion del modo previo — especialmente el re-apply del
      // layout floating cuando previousMode === 'floating'.
      this.toggleFullscreenMode()
    },

    onCameraFocus(camera) {
      // Maximiza la cámara reusando su MISMO iframe (CSS-promote): solo
      // marcamos maximizedId; el template promueve la celda a fixed y la
      // raíz .camera-tray neutraliza su backdrop-filter. Cero recarga del
      // stream (no se monta ningún overlay/iframe nuevo).
      this.maximizedId = camera.id
    },

    closeMaximized() {
      this.maximizedId = null
    },

    // Reconcilia `cells` desde las props `cameras` (fijadas) y `prewarmCameras`
    // (precalentadas del PMI). Reglas para NO recargar iframes:
    //  - APPEND-only: las celdas nuevas se agregan al final; nunca se reordena.
    //  - Flag `pinned` se voltea IN-PLACE (mismo id, misma posición) → fijar
    //    solo cambia display, no mueve el nodo.
    //  - Se quita una celda solo si su cámara ya no está en NINGUNA fuente
    //    (p.ej. cambió el PMI y no estaba fijada) → su iframe se desmonta.
    // El streamUrl es determinístico por cámara, así que refrescar cell.camera
    // no cambia el :src → no recarga.
    _reconcileCells() {
      var pinned = this.cameras || []
      var prewarm = this.prewarmCameras || []
      var byId = {}
      pinned.forEach(function(c) { byId[c.id] = { src: c, pinned: true } })
      prewarm.forEach(function(c) { if (!byId[c.id]) byId[c.id] = { src: c, pinned: false } })

      // 1. Quitar celdas que ya no están en ninguna fuente.
      this.cells = this.cells.filter(function(cell) { return byId[cell.id] })

      // 2. Actualizar flag + datos in-place (sin reordenar).
      var existing = {}
      this.cells.forEach(function(cell) {
        var entry = byId[cell.id]
        cell.pinned = entry.pinned
        cell.camera = entry.src
        existing[cell.id] = true
      })

      // 3. Agregar nuevas con append (fijadas primero, luego precalentadas).
      var self = this
      pinned.forEach(function(c) {
        if (!existing[c.id]) { self.cells.push({ id: c.id, camera: c, pinned: true }); existing[c.id] = true }
      })
      prewarm.forEach(function(c) {
        if (!existing[c.id]) { self.cells.push({ id: c.id, camera: c, pinned: false }); existing[c.id] = true }
      })
    },

    onCameraReload(camera) {
      // Incrementar reloadKeys[camera.id] fuerza re-mount del iframe
      // por el :key dinamico, lo que recarga el feed. Se usa $set para
      // que Vue 2 detecte el cambio en una nueva key del objeto.
      const current = this.reloadKeys[camera.id] || 0
      this.$set(this.reloadKeys, camera.id, current + 1)
    },

    toggleCollapseCamera(camera) {
      // Toggle del estado colapsado de una cámara individual: la celda
      // mantiene su slot pero el iframe se desmonta (libera ancho de banda
      // y CPU). Al expandir, el :key del iframe usa reloadKeys[camera.id]
      // así que arranca un stream fresco.
      const next = !this.collapsedById[camera.id]
      this.$set(this.collapsedById, camera.id, next)
    },

    _elevateParentZIndex() {
      const root = this.$refs.root
      if (!root || !root.parentElement || this._parentElevated) return
      // Guardar el valor inline original (generalmente "" porque el z-index
      // de .zone-tray viene del stylesheet, no inline)
      this._originalParentZIndex = root.parentElement.style.zIndex
      root.parentElement.style.zIndex = '1000'
      this._parentElevated = true
    },

    _restoreParentZIndex() {
      if (!this._parentElevated) return
      const root = this.$refs.root
      if (root && root.parentElement) {
        root.parentElement.style.zIndex = this._originalParentZIndex || ''
      }
      this._originalParentZIndex = undefined
      this._parentElevated = false
    },

    onTrayMouseDown(e) {
      if (!this.isFloating) return
      // Permitir drag desde CUALQUIER area del tray excepto elementos
      // interactivos o contenido (camaras con iframes, botones, resize handles).
      // Las CELLS VACIAS (.camera-cell--empty) SI permiten drag — son espacio
      // vacio que el usuario espera poder arrastrar. Por eso :not().
      const excluded = 'button, input, select, textarea, a, ' +
                       '.v-btn, .v-input, ' +
                       '.resize-handle, .camera-drag-handle, ' +
                       '.camera-cell:not(.camera-cell--empty), iframe'
      if (e.target.closest(excluded)) return
      e.preventDefault()
      const pos = this.trayFloatingPos || { x: 100, y: 100 }
      this._dragOffset = {
        x: e.clientX - pos.x,
        y: e.clientY - pos.y,
        // Ultima posicion calculada — se commitea al store en dragEnd
        currentX: pos.x,
        currentY: pos.y,
      }
      this.isInteracting = true
      // Forzar cursor grabbing en todo el viewport (sobreescribe el cursor
      // del overlay que heredaria el grab del tray padre).
      document.body.style.cursor = 'grabbing'
      document.addEventListener('mousemove', this._onDragMove)
      document.addEventListener('mouseup', this._onDragEnd)
    },

    // Nota: el nombre del metodo es _onDragMoveImpl porque el handler
    // pre-bound (creado en `created`) se llama _onDragMove. Esto evita
    // que Vue re-asigne la funcion del option object al que esta en `this`.
    _onDragMoveImpl(e) {
      if (!this._dragOffset) return
      // Clamp para no dejar el tray fuera del viewport (siempre MIN_VISIBLE visible)
      const maxX = Math.max(0, window.innerWidth - MIN_VISIBLE_X)
      const maxY = Math.max(0, window.innerHeight - MIN_VISIBLE_Y)
      const x = Math.max(0, Math.min(maxX, e.clientX - this._dragOffset.x))
      const y = Math.max(0, Math.min(maxY, e.clientY - this._dragOffset.y))
      // Aplicar directo al DOM durante el drag — sin commits a Vuex.
      // Commiteamos al final en _onDragEnd. Esto evita escribir a localStorage
      // a 60 Hz y re-renderizar el componente en cada frame.
      const root = this.$refs.root
      if (root) {
        root.style.left = x + 'px'
        root.style.top = y + 'px'
      }
      this._dragOffset.currentX = x
      this._dragOffset.currentY = y
    },

    _onDragEndImpl() {
      if (this._dragOffset) {
        // Commit una sola vez al final para persistir en localStorage
        this.$store.commit('map-v2/SET_TRAY_FLOATING_POS', {
          x: this._dragOffset.currentX,
          y: this._dragOffset.currentY,
        })
      }
      this._dragOffset = null
      this.isInteracting = false
      document.body.style.cursor = ''
      document.removeEventListener('mousemove', this._onDragMove)
      document.removeEventListener('mouseup', this._onDragEnd)
    },

    // ─── Custom resize desde 8 direcciones ──────────────────
    _startResize(direction, e) {
      if (!this.isFloating) return
      e.preventDefault()
      e.stopPropagation()
      const root = this.$refs.root
      if (!root) return
      // Leer startX/startY del STORE (source of truth), no del DOM rect.
      // Si lees del rect, puedes desfasarte porque rootStyle aplica left/top
      // desde el store y pequeños offsets acumulan error entre interacciones.
      // Leer size del DOM porque ese SI es la source of truth durante drag.
      const pos = this.trayFloatingPos || { x: 100, y: 100 }
      const rect = root.getBoundingClientRect()
      this._resizeState = {
        dir: direction,
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        startX: pos.x,
        startY: pos.y,
        startW: rect.width,
        startH: rect.height,
        // Ultima geometria calculada — se commitea al store en resizeEnd
        currentX: pos.x,
        currentY: pos.y,
        currentW: rect.width,
        currentH: rect.height,
      }
      this.isInteracting = true
      // Forzar el cursor del resize en todo el body para que el overlay
      // full-viewport no muestre un cursor incorrecto (heredaria el grab
      // del tray padre).
      document.body.style.cursor = direction + '-resize'
      document.addEventListener('mousemove', this._onResizeMove)
      document.addEventListener('mouseup', this._onResizeEnd)
    },

    _onResizeMoveImpl(e) {
      if (!this._resizeState) return
      const s = this._resizeState
      let dx = e.clientX - s.startMouseX
      let dy = e.clientY - s.startMouseY
      const maxW = Math.floor(window.innerWidth * TRAY_MAX_VW)
      const maxH = Math.floor(window.innerHeight * TRAY_MAX_VH)
      let newX = s.startX
      let newY = s.startY
      let newW = s.startW
      let newH = s.startH

      // Horizontal
      if (s.dir.indexOf('e') !== -1) {
        newW = Math.max(TRAY_MIN_W, Math.min(maxW, s.startW + dx))
      } else if (s.dir.indexOf('w') !== -1) {
        let tentativeW = s.startW - dx
        if (tentativeW < TRAY_MIN_W) {
          tentativeW = TRAY_MIN_W
          dx = s.startW - TRAY_MIN_W
        } else if (tentativeW > maxW) {
          tentativeW = maxW
          dx = s.startW - maxW
        }
        newW = tentativeW
        newX = s.startX + dx
      }

      // Vertical
      if (s.dir.indexOf('s') !== -1) {
        newH = Math.max(TRAY_MIN_H, Math.min(maxH, s.startH + dy))
      } else if (s.dir.indexOf('n') !== -1) {
        let tentativeH = s.startH - dy
        if (tentativeH < TRAY_MIN_H) {
          tentativeH = TRAY_MIN_H
          dy = s.startH - TRAY_MIN_H
        } else if (tentativeH > maxH) {
          tentativeH = maxH
          dy = s.startH - maxH
        }
        newH = tentativeH
        newY = s.startY + dy
      }

      // Clamp del borde top-left para mantener el tray dentro del viewport
      newX = Math.max(0, Math.min(window.innerWidth - MIN_VISIBLE_X, newX))
      newY = Math.max(0, Math.min(window.innerHeight - MIN_VISIBLE_Y, newY))

      // Aplicar directo al DOM (sin commits a Vuex durante el movimiento)
      const root = this.$refs.root
      if (!root) return
      root.style.width = newW + 'px'
      root.style.height = newH + 'px'
      root.style.left = newX + 'px'
      root.style.top = newY + 'px'

      s.currentX = newX
      s.currentY = newY
      s.currentW = newW
      s.currentH = newH
    },

    _onResizeEndImpl() {
      if (this._resizeState) {
        // Commit una sola vez al final para persistir
        const s = this._resizeState
        this.$store.commit('map-v2/SET_TRAY_FLOATING_POS', { x: s.currentX, y: s.currentY })
        this.$store.commit('map-v2/SET_TRAY_FLOATING_SIZE', { w: s.currentW, h: s.currentH })
      }
      this._resizeState = null
      this.isInteracting = false
      document.body.style.cursor = ''
      document.removeEventListener('mousemove', this._onResizeMove)
      document.removeEventListener('mouseup', this._onResizeEnd)
    },

    // ─── Popout mode (ventana independiente) ────────────────
    togglePopoutMode() {
      if (this.isPopout) {
        this._closePopoutWindow()
      } else {
        this._openPopoutWindow()
      }
    },

    _openPopoutWindow() {
      // Posicion inicial: centrada en el viewport actual, parte inferior
      const w = 1200
      const h = 500
      const left = window.screenX + Math.max(0, Math.floor((window.innerWidth - w) / 2))
      const top = window.screenY + Math.max(0, window.innerHeight - h - 80)
      const features = 'width=' + w + ',height=' + h +
                       ',left=' + left + ',top=' + top +
                       ',resizable=yes,scrollbars=yes,location=no,menubar=no,toolbar=no,status=no'
      const win = window.open('', 'kalan-camera-tray', features)
      if (!win) {
        window.alert('El navegador bloqueo el popup. Habilitalo para este sitio e intenta de nuevo.')
        return
      }
      sharedPopoutWindow = win
      // Si el tray estaba en floating, limpiar estado de floating primero
      if (this.isFloating) {
        if (this.$refs.root) {
          this.$refs.root.style.width = ''
          this.$refs.root.style.height = ''
          this.$refs.root.style.left = ''
          this.$refs.root.style.top = ''
        }
        this._restoreParentZIndex()
      }
      this.$store.commit('map-v2/SET_TRAY_MODE', 'popout')
      this._buildPopoutContent(win)
      this._startPopoutPoll()
    },

    // Inicia el poll que detecta cuando el usuario cierra la ventana popout
    // desde el boton X del browser. Si no hay componente montado, el poll
    // no existe — pero el popout sigue vivo. Al re-montar, el componente
    // reinicia el poll (ver mounted).
    _startPopoutPoll() {
      if (sharedPopoutPoll) return  // ya existe
      sharedPopoutPoll = window.setInterval(() => {
        if (!sharedPopoutWindow || sharedPopoutWindow.closed) {
          this._handlePopoutClosed()
        }
      }, 500)
    },

    _handlePopoutClosed() {
      if (sharedPopoutPoll) {
        window.clearInterval(sharedPopoutPoll)
        sharedPopoutPoll = null
      }
      sharedPopoutWindow = null
      // Solo commit si todavia estamos en modo popout (evita loops)
      if (this.isPopout) {
        this.$store.commit('map-v2/SET_TRAY_MODE', 'docked')
      }
    },

    _closePopoutWindow() {
      if (sharedPopoutPoll) {
        window.clearInterval(sharedPopoutPoll)
        sharedPopoutPoll = null
      }
      if (sharedPopoutWindow && !sharedPopoutWindow.closed) {
        try { sharedPopoutWindow.close() } catch (_) { /* ignore */ }
      }
      sharedPopoutWindow = null
      if (this.isPopout) {
        this.$store.commit('map-v2/SET_TRAY_MODE', 'docked')
      }
    },

    // Actualiza SOLO la cuadrícula y las posiciones de los tiles del popout, sin
    // recrear iframes (no corta streams). Se usa al cambiar el layout (cols/
    // mover/redimensionar) cuando el SET de cámaras no cambió. Ante cualquier
    // desajuste, reconstruye todo por seguridad.
    _syncPopoutLayout(win) {
      const doc = win.document
      if (!doc || !doc.body) return
      const grid = doc.querySelector('.popout-grid')
      const cameras = this.cameras || []
      if (!grid) { this._buildPopoutContent(win); return }
      const tilesEls = grid.querySelectorAll('.popout-tile')
      if (tilesEls.length !== cameras.length) { this._buildPopoutContent(win); return }
      grid.style.gridTemplateColumns = 'repeat(' + this.gridCols + ', 1fr)'
      const positions = this.packed.positions
      for (let i = 0; i < tilesEls.length; i += 1) {
        const el = tilesEls[i]
        const pos = positions[el.getAttribute('data-camera-id')]
        el.style.gridColumn = pos ? (pos.col + ' / span ' + pos.w) : ''
        el.style.gridRow = pos ? (pos.row + ' / span ' + pos.h) : ''
      }
    },

    // Construye el DOM de la ventana popout desde cero.
    // Usamos document.createElement (no innerHTML) para evitar XSS con
    // contenido dinamico (nombres de camaras, etc).
    _buildPopoutContent(win) {
      const doc = win.document
      const cameras = this.cameras || []
      const packedPos = this.packed.positions
      const cols = this.gridCols
      doc.title = 'Kalan — ' + cameras.length + ' camaras en vivo'

      // Limpiar contenido previo (por si es un rebuild)
      while (doc.body && doc.body.firstChild) {
        doc.body.removeChild(doc.body.firstChild)
      }
      while (doc.head && doc.head.firstChild) {
        doc.head.removeChild(doc.head.firstChild)
      }

      // Estilos inline del popout
      const style = doc.createElement('style')
      style.textContent = [
        'html, body { margin: 0; padding: 0; height: 100%; }',
        'body {',
        '  background: var(--kln-bg-deep); color: var(--kln-text);',
        '  font-family: "Public Sans", -apple-system, BlinkMacSystemFont, sans-serif;',
        '  display: flex; flex-direction: column; overflow: hidden;',
        '}',
        '.popout-header {',
        '  display: flex; justify-content: space-between; align-items: center;',
        '  padding: 8px 16px;',
        '  border-bottom: 1px solid var(--kln-border-dim);',
        '  flex-shrink: 0;',
        '}',
        '.popout-title {',
        '  font-size: 12px; font-weight: 600;',
        '  color: var(--kln-text-muted);',
        '  text-transform: uppercase; letter-spacing: 0.04em;',
        '  display: flex; align-items: center; gap: 8px;',
        '}',
        '.popout-count {',
        '  font-size: 10px; font-weight: 500;',
        '  color: var(--kln-text-dim);',
        '  text-transform: none; letter-spacing: 0;',
        '}',
        '.popout-grid {',
        '  flex: 1; overflow-y: auto;',
        '  display: grid;',
        '  grid-template-columns: repeat(' + cols + ', 1fr);',
        '  grid-auto-rows: 1fr;',
        '  gap: 6px; padding: 8px;',
        '  align-content: start;',
        '  background: rgba(0, 0, 0, 0.3);',
        '}',
        '.popout-tile {',
        '  position: relative; background: #0a0a0f;',
        '  border-radius: 6px; overflow: hidden;',
        '  aspect-ratio: 16 / 9;',
        '}',
        '.popout-tile iframe {',
        '  position: absolute; inset: 0;',
        '  width: 100%; height: 100%;',
        '  border: none; background: #000;',
        '}',
        '.popout-label {',
        '  position: absolute; top: 4px; left: 4px; z-index: 2;',
        '  background: rgba(0, 0, 0, 0.6);',
        '  padding: 2px 8px; border-radius: 4px;',
        '  font-size: 10px; color: rgba(255, 255, 255, 0.9);',
        '  pointer-events: none;',
        '  max-width: calc(100% - 16px);',
        '  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;',
        '}',
        '.popout-reload {',
        '  position: absolute; top: 4px; right: 4px; z-index: 3;',
        '  background: rgba(0, 0, 0, 0.6);',
        '  color: #fff; border: none; border-radius: 4px;',
        '  width: 22px; height: 22px;',
        '  font-size: 14px; font-weight: bold; line-height: 1;',
        '  cursor: pointer; opacity: 0;',
        '  transition: opacity 0.15s, background 0.15s;',
        '  display: flex; align-items: center; justify-content: center;',
        '}',
        '.popout-tile:hover .popout-reload { opacity: 1; }',
        '.popout-reload:hover { background: rgba(0, 0, 0, 0.85); }',
        '.popout-empty {',
        '  flex: 1; display: flex; align-items: center; justify-content: center;',
        '  color: var(--kln-text-dim); font-size: 13px;',
        '}',
        '.popout-grid::-webkit-scrollbar { width: 6px; }',
        '.popout-grid::-webkit-scrollbar-thumb {',
        '  background: rgba(255, 255, 255, 0.15); border-radius: 3px;',
        '}',
      ].join('\n')
      doc.head.appendChild(style)

      // Header
      const header = doc.createElement('div')
      header.className = 'popout-header'
      const titleEl = doc.createElement('div')
      titleEl.className = 'popout-title'
      titleEl.appendChild(doc.createTextNode('CAMARAS EN VIVO'))
      const countEl = doc.createElement('span')
      countEl.className = 'popout-count'
      countEl.appendChild(doc.createTextNode(cameras.length + ' / 36'))
      titleEl.appendChild(countEl)
      header.appendChild(titleEl)
      doc.body.appendChild(header)

      if (cameras.length === 0) {
        const empty = doc.createElement('div')
        empty.className = 'popout-empty'
        empty.appendChild(doc.createTextNode('Sin camaras pineadas'))
        doc.body.appendChild(empty)
        return
      }

      // Grid
      const grid = doc.createElement('div')
      grid.className = 'popout-grid'
      doc.body.appendChild(grid)

      // URLs relativas del streamUrl necesitan convertirse a absolutas
      // porque dentro del popout (about:blank) no hay base URL.
      const baseOrigin = window.location.origin

      for (const cam of cameras) {
        const tile = doc.createElement('div')
        tile.className = 'popout-tile'
        // data-camera-id permite a _syncPopoutLayout reubicar el tile sin recrear
        // su iframe (evita cortar el stream al mover/redimensionar en el tray).
        tile.setAttribute('data-camera-id', cam.id)
        const pos = packedPos[cam.id]
        if (pos) {
          tile.style.gridColumn = pos.col + ' / span ' + pos.w
          tile.style.gridRow = pos.row + ' / span ' + pos.h
        }

        const label = doc.createElement('div')
        label.className = 'popout-label'
        label.appendChild(doc.createTextNode(cam.name || 'Camara'))
        tile.appendChild(label)

        if (cam.streamUrl) {
          const iframe = doc.createElement('iframe')
          let url = cam.streamUrl
          // Convertir a URL absoluta si es relativa
          if (url.charAt(0) === '/') {
            url = baseOrigin + url
          }
          iframe.src = url
          iframe.setAttribute('allow', 'autoplay')
          iframe.setAttribute('frameborder', '0')
          tile.appendChild(iframe)

          // Boton de reload por tile. Se agrega al mismo `tile` (position
          // relative) con position absolute top-right. Al click, forzamos
          // recarga directa del iframe via `iframe.src = iframe.src` — es
          // el truco estandar que no requiere gestionar keys en este
          // contexto vanilla DOM. stopPropagation defensivo.
          const reloadBtn = doc.createElement('button')
          reloadBtn.type = 'button'
          reloadBtn.className = 'popout-reload'
          reloadBtn.title = 'Recargar camara'
          reloadBtn.textContent = '\u21BB' // caracter unicode ↻
          reloadBtn.addEventListener('click', function (e) {
            e.stopPropagation()
            // eslint-disable-next-line no-self-assign
            iframe.src = iframe.src
          })
          tile.appendChild(reloadBtn)
        }

        grid.appendChild(tile)
      }
    },
  },
}
</script>

<style scoped>
.camera-tray {
  display: flex;
  flex-direction: column;
  background: var(--kln-bg-surface);
  border: 1px solid var(--kln-border-normal);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  width: 100%;
  height: 100%;
  pointer-events: all;
}

/* Modo flotante: panel independiente draggable + resizable */
.camera-tray--floating {
  /* position/left/top vienen de rootStyle inline;
     width/height son seteados directamente por JS. */
  height: auto;  /* override docked height: 100% — JS controla el alto directamente */
  background: var(--kln-bg-overlay);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-top: none;  /* el border-top viene del modo docked, se quita en floating */
  overflow: visible; /* para que los resize handles salgan del borde */
  min-width: 480px;  /* TRAY_MIN_W del script */
  min-height: 220px; /* TRAY_MIN_H del script */
  /* Cursor grab por default en TODA la superficie del tray flotante.
     Las areas interactivas (botones, cells, resize handles) lo sobrescriben. */
  cursor: grab;
  user-select: none;
}

.camera-tray--floating:active {
  cursor: grabbing;
}

/* Cells de camaras: cursor default (contenido, no draggable) */
.camera-tray--floating .camera-cell,
.camera-tray--floating .camera-cell--empty {
  cursor: default;
}

/* Botones y controles interactivos: cursor pointer */
.camera-tray--floating button,
.camera-tray--floating .v-btn,
.camera-tray--floating input {
  cursor: pointer;
}

/* Overlay full-viewport durante drag/resize. Sin este overlay, cuando el
   cursor pasa sobre un iframe de camara, el iframe "traga" los mousemove/
   mouseup y el drag/resize queda stuck. El overlay tiene pointer-events auto
   para catchar los eventos encima de los iframes y dejar que burbujeen al
   listener de document. z-index muy alto para cubrirlo todo. */
.tray-interaction-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: transparent;
  cursor: inherit;
}

/* ─── Modo popout: tray colapsado a un chip flotante ────────
   Cuando las camaras estan en ventana independiente, el tray local se
   colapsa completamente para devolver todo el espacio vertical a los
   sidebars. Solo queda un chip pequeno centrado en el bottom que
   permite traer las camaras de vuelta. */
.camera-tray--popout {
  position: fixed;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  width: auto;
  height: auto;  /* override docked height: 100% */
  min-width: 0;
  min-height: 0;
  background: none;
  border: none;
  border-top: none;
  box-shadow: none;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  z-index: 200;
  pointer-events: none; /* el chip re-activa pointer-events */
}

.popout-reminder-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px 6px 10px;
  background: var(--kln-bg-overlay);
  border: 1px solid var(--kln-accent-glow);
  border-radius: 999px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  cursor: pointer;
  pointer-events: all;
  transition: background 0.15s, border-color 0.15s, transform 0.15s;
  user-select: none;
}

.popout-reminder-chip:hover {
  background: var(--kln-bg-surface);
  border-color: var(--kln-accent);
  transform: translateY(-1px);
}

.popout-reminder-label {
  font-size: 11px;
  font-weight: 500;
  color: var(--kln-text-muted);
  letter-spacing: 0.01em;
}

.popout-reminder-action {
  font-size: 11px;
  font-weight: 600;
  color: var(--kln-accent);
  padding-left: 8px;
  border-left: 1px solid var(--kln-accent-soft);
}

.popout-reminder-chip:hover .popout-reminder-action {
  color: var(--kln-accent-bright);
}

/* ─── Resize handles (8 direcciones) ─────────────────────── */
/* Invisibles por default, cambian cursor al hover y muestran tint morado. */
.resize-handle {
  position: absolute;
  background: transparent;
  transition: background 0.15s;
  z-index: 10;
}

.resize-handle:hover {
  background: var(--kln-accent-soft);
}

/* Bordes (N, E, S, W) — extendidos ligeramente fuera del contenedor */
.resize-handle--n {
  top: -4px;
  left: 14px;
  right: 14px;
  height: 8px;
  cursor: n-resize;
}

.resize-handle--e {
  top: 14px;
  right: -4px;
  bottom: 14px;
  width: 8px;
  cursor: e-resize;
}

.resize-handle--s {
  bottom: -4px;
  left: 14px;
  right: 14px;
  height: 8px;
  cursor: s-resize;
}

.resize-handle--w {
  top: 14px;
  left: -4px;
  bottom: 14px;
  width: 8px;
  cursor: w-resize;
}

/* Esquinas (NW, NE, SW, SE) */
.resize-handle--nw {
  top: -4px;
  left: -4px;
  width: 18px;
  height: 18px;
  cursor: nw-resize;
  border-top-left-radius: 12px;
}

.resize-handle--ne {
  top: -4px;
  right: -4px;
  width: 18px;
  height: 18px;
  cursor: ne-resize;
  border-top-right-radius: 12px;
}

.resize-handle--sw {
  bottom: -4px;
  left: -4px;
  width: 18px;
  height: 18px;
  cursor: sw-resize;
  border-bottom-left-radius: 12px;
}

.resize-handle--se {
  bottom: -4px;
  right: -4px;
  width: 18px;
  height: 18px;
  cursor: se-resize;
  border-bottom-right-radius: 12px;
}

/* Indicador visual permanente en la esquina SE (grip lines) para
   "avisar" que el tray se puede resizear. Es decorativo, no bloquea clicks. */
.resize-handle--se::after {
  content: '';
  position: absolute;
  bottom: 3px;
  right: 3px;
  width: 10px;
  height: 10px;
  pointer-events: none;
  background-image:
    linear-gradient(135deg, transparent 30%, rgba(255, 255, 255, 0.4) 30%, rgba(255, 255, 255, 0.4) 40%, transparent 40%, transparent 55%, rgba(255, 255, 255, 0.4) 55%, rgba(255, 255, 255, 0.4) 65%, transparent 65%, transparent 80%, rgba(255, 255, 255, 0.4) 80%, rgba(255, 255, 255, 0.4) 90%, transparent 90%);
}

.tray-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  border-bottom: 1px solid var(--kln-border-dim);
  flex-shrink: 0;
}

.tray-header--draggable {
  cursor: grab;
  user-select: none;
}

.tray-header--draggable:active {
  cursor: grabbing;
}

/* Estilo del boton "flotante" cuando esta activo */
.tray-btn-active {
  color: var(--kln-accent) !important;
  background: var(--kln-accent-soft) !important;
}

.tray-title {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: var(--kln-text-muted);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.tray-count {
  margin-left: 8px;
  font-size: 10px;
  font-weight: 500;
  color: var(--kln-text-dim);
  text-transform: none;
  letter-spacing: 0;
}

.tray-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.layout-toggle {
  background: transparent !important;
}

.layout-toggle .v-btn {
  color: var(--kln-text-dim) !important;
  height: 24px !important;
  min-width: 28px !important;
}

.layout-toggle .v-btn--active {
  color: var(--kln-text) !important;
}

.tray-close-btn {
  color: var(--kln-text-dim) !important;
}

/* Grid container (scroll vertical) — altura y layout via inline style computed */
.tray-grid-container {
  scrollbar-width: thin;
  scrollbar-color: var(--kln-border-dim) transparent;
}

.tray-grid-container::-webkit-scrollbar {
  width: 6px;
}

.tray-grid-container::-webkit-scrollbar-thumb {
  background: var(--kln-border-dim);
  border-radius: 3px;
}

.tray-grid-container::-webkit-scrollbar-track {
  background: transparent;
}

/* Wrappers transparentes al grid: sus hijos (camera-cell / empty-cell)
   se renderizan como si fueran directos del grid. Necesarios para aislar
   el diff de Vue 2 entre los dos v-for y prevenir moves de iframes que
   causan reload en Chrome al quitar una cámara. */
.tray-grid__cameras,
.tray-grid__empties {
  display: contents;
}

/* Camera cell — altura fija via grid-auto-rows, el wrapper interno hace letterboxing */
.camera-cell {
  position: relative;
  background: #0a0a0f;
  overflow: hidden;
  border-radius: 4px;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Celda precalentada (no fijada): OCULTA pero MONTADA → el <iframe> carga y el
   stream se mantiene caliente en background. Al fijar se quita esta clase →
   aparece al instante sin recargar. display:none la saca del flujo del grid,
   así que no ocupa slot ni afecta el layout de las fijadas. NO usar v-if (eso
   desmontaría el iframe y perderíamos el pre-warm). */
.camera-cell--prewarm-hidden {
  display: none !important;
}

/* Wrapper con dimensiones calculadas via el método cellAspectStyle(cameraId).
   El alto/ancho exactos se aplican inline para evitar los problemas de
   aspect-ratio CSS dentro de flex containers. */
.camera-aspect {
  /* position y dimensiones vienen de inline style */
  background: #000;
  overflow: hidden;
}

.camera-cell--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--kln-border-dim);
  border: 1px dashed var(--kln-border-dim);
  border-radius: 4px;
}

.camera-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  background: #000;
  border: none;
}

.camera-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 4px;
  background: rgba(0, 0, 0, 0.6);
  z-index: 2;
  pointer-events: auto;
}

.camera-name {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.85);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.camera-remove-btn {
  color: rgba(255, 255, 255, 0.7) !important;
  flex-shrink: 0;
}

.camera-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
}

.camera-error {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: rgba(0, 0, 0, 0.6);
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
}

/* ── PTZ controls ── */
.ptz-controls {
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: flex;
  gap: 6px;
  opacity: 0.7;
  z-index: 2;
}

.ptz-dpad {
  width: 80px;
}

.ptz-zoom {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* ─── Modo maximizado (cámara individual a pantalla completa) ──────
   CSS-promote: la MISMA celda (con su mismo <iframe>) se promueve a
   position:fixed inset:0. El stream NO se recarga porque el iframe no se
   recrea ni cambia de padre. Para que el fixed escape al viewport, la raíz
   .camera-tray neutraliza su backdrop-filter (que crea un containing block
   para descendientes fixed en Chrome/WebKit). El backdrop queda tapado por
   esta vista, así que neutralizarlo es invisible. */
.camera-tray--has-maximized {
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

.camera-cell--maximized {
  position: fixed !important;
  inset: 0 !important;
  z-index: 9500;
  margin: 0 !important;
  border-radius: 0 !important;
  background: rgba(0, 0, 0, 0.96);
  display: flex !important;
  align-items: center;
  justify-content: center;
}

/* La celda maximizada centra un letterbox 16:9 que cabe debajo del header
   (~44px) + padding. Sobrescribimos las dimensiones inline (cellAspectStyle)
   con !important, ya que el inline style las calcula para el tamaño del grid. */
.camera-cell--maximized .camera-aspect {
  position: relative !important;
  width: 100% !important;
  height: auto !important;
  max-width: calc((100vh - 92px) * 16 / 9) !important;
  max-height: calc(100vh - 92px) !important;
  aspect-ratio: 16 / 9;
}

/* El overlay de hover (nombre + botones chicos) se oculta: el header
   maximizado lo reemplaza. */
.camera-cell--maximized .camera-overlay {
  display: none !important;
}

/* PTZ un poco más grande y separado del borde en pantalla completa. */
.camera-cell--maximized .ptz-controls {
  bottom: 32px;
  right: 32px;
  opacity: 1;
}

/* Header del modo maximizado — mismo look que el overlay anterior. */
.camera-maximized-header {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 18px;
  background: rgba(10, 14, 26, 0.85);
  border-bottom: 1px solid var(--kln-border-dim);
}

.camera-maximized-title {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: var(--kln-text);
  letter-spacing: 0.02em;
  min-width: 0;
}

.camera-maximized-title span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.camera-maximized-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.camera-maximized-actions .v-btn {
  color: var(--kln-text-muted) !important;
}

.camera-maximized-actions .v-btn:hover {
  color: var(--kln-text) !important;
  background: var(--kln-border-dim) !important;
}

/* ─── Modo fullscreen (videowall) ─────────────────────────
   El tray cubre TODO el viewport del navegador (inset: 0).
   Tapa incluso el topbar de Vuexy; si el usuario quiere area
   extra tiene el fullscreen del browser en la toolbar.
   El header del tray sigue visible con sus controles (cambiar
   layout, salir de fullscreen) — necesario para uso operativo. */
.camera-tray--fullscreen {
  position: fixed !important;
  /* Arranca debajo del cintillo de incidentes (--kln-ticker-height = 48px cuando
     está visible, 0px cuando no) para no taparlo en pantalla completa. */
  top: var(--kln-ticker-height, 0px);
  right: 0;
  bottom: 0;
  left: 0;
  width: 100vw !important;
  height: calc(100vh - var(--kln-ticker-height, 0px)) !important;
  z-index: 9000;
  background: rgba(6, 9, 18, 0.98);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border: none;
  border-top: none;
  border-radius: 0;
  box-shadow: none;
  margin: 0;
  padding: 0;
}

/* El grid ocupa todo el espacio debajo del header via flex.
   NO usamos calc(100vh - altura_header) para evitar acoplarnos
   a un numero magico: flex: 1 + min-height: 0 deja que el
   browser calcule el alto disponible automaticamente. */
.camera-tray--fullscreen .tray-grid-container {
  flex: 1;
  min-height: 0;
  max-height: none;
  height: auto;
}

/* El grid interior usa el MISMO alto de fila 16:9 que docked/floating
   (grid-auto-rows inline = ancho_columna × 9/16). NO se estira con 1fr:
   el operador pidió que cada cámara conserve su tamaño/ratio en vez de
   ocupar todo el alto de la columna. Si sobran cámaras hay scroll; si
   faltan, queda espacio abajo (align-content: start, vía gridStyle). */
.camera-tray--fullscreen .tray-grid {
  height: 100%;
}

/* Boton de focus por camara. Vive dentro de .camera-overlay (flex con
   justify-content: space-between), asi que es un flex item normal al
   lado del .camera-remove-btn. Visible solo en hover de la celda. */
.camera-focus-btn {
  color: rgba(255, 255, 255, 0.7) !important;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s;
}

.camera-cell:hover .camera-focus-btn {
  opacity: 1;
}

/* Boton de reload por camara. Mismo patron que .camera-focus-btn:
   flex item dentro de .camera-overlay, visible solo en hover. */
.camera-reload-btn {
  color: rgba(255, 255, 255, 0.7) !important;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s;
}

.camera-cell:hover .camera-reload-btn {
  opacity: 1;
}

/* Boton del selector visual de tamaño. Mismo patron de hover. */
.camera-size-btn {
  color: rgba(255, 255, 255, 0.7) !important;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s;
}

.camera-cell:hover .camera-size-btn {
  opacity: 1;
}

/* Boton de colapsar/expandir por camara. Mismo patron de hover que reload/
   focus, EXCEPTO cuando la cámara ya está colapsada: en ese caso el botón
   queda visible permanentemente porque es la única forma de restaurarla. */
.camera-collapse-btn {
  color: rgba(255, 255, 255, 0.7) !important;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s;
}

.camera-cell:hover .camera-collapse-btn,
.camera-cell--collapsed .camera-collapse-btn {
  opacity: 1;
}

/* Cámara colapsada: el iframe se desmonta y un placeholder ocupa el slot
   para preservar el layout del grid. El fondo es más opaco y el overlay
   superior gana presencia visual (la cámara minimizada no debe pasar como
   "rota" — es estado intencional del operador). */
.camera-cell--collapsed {
  background: rgba(10, 10, 15, 0.95);
}

.camera-cell--collapsed .camera-aspect {
  background: transparent;
}

.camera-cell--collapsed .camera-overlay {
  background: rgba(0, 0, 0, 0.75);
}

.camera-collapsed-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.camera-collapsed-label {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.35);
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.camera-drag-handle {
  cursor: grab;
  display: inline-flex;
  align-items: center;
  margin-right: auto;
  color: rgba(255, 255, 255, 0.7);
  padding: 0 2px;
}
.camera-drag-handle:active { cursor: grabbing; }

/* Celda en movimiento durante el arrastre: se atenúa para indicar que se está
   reubicando, mientras el resalte de destino marca dónde caerá. */
.camera-cell--dragging {
  opacity: 0.4;
  outline: 2px solid var(--kln-accent);
  outline-offset: -2px;
}

/* Resalte de la celda destino mientras se arrastra (posición vía grid inline). */
.drag-target-highlight {
  border: 2px dashed var(--kln-accent);
  background: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  pointer-events: none;
  z-index: 5;
  align-self: stretch;
  justify-self: stretch;
}

/* Selector visual de tamaño (mini-cuadrícula tipo "insertar tabla"). */
.size-picker {
  background: var(--kln-bg-deep);
  padding: 8px;
  border-radius: 6px;
}
.size-picker__caption {
  font-size: 10px;
  color: var(--kln-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
}
.size-picker__row { display: flex; }
.size-picker__cell {
  width: 22px;
  height: 16px;
  margin: 2px;
  border: 1px solid var(--kln-border-dim);
  border-radius: 2px;
  cursor: pointer;
  transition: background 0.08s, border-color 0.08s;
}
.size-picker__cell--on {
  background: var(--kln-accent);
  border-color: var(--kln-accent);
}
.size-picker__label {
  margin-top: 6px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: var(--kln-text);
}

.preset-menu { min-width: 240px; max-width: 320px; }
.preset-menu .preset-active { background: rgba(255, 255, 255, 0.06); }
.tray-preset-name { font-size: 11px; color: var(--kln-accent); margin-left: 4px; }
.tray-preset-name--mod { color: var(--kln-warning, #F39C12); }
.preset-save-row { display: flex; align-items: center; gap: 4px; padding: 4px 8px; }
.preset-name-input { font-size: 13px; }
</style>
